import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, ArrowUpDown, GripVertical } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function SortablePage({ page, index }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`cursor-grab active:cursor-grabbing overflow-hidden dark:bg-slate-800 ${isDragging ? 'shadow-xl' : ''}`}
    >
      <div className="relative" {...attributes} {...listeners}>
        <img
          src={page.dataUrl}
          alt={`Page ${page.originalPageNum}`}
          className="w-full h-auto"
        />
        <div className="absolute top-2 left-2">
          <Badge className="bg-black/70">{index + 1}</Badge>
        </div>
        <div className="absolute top-2 right-2">
          <Badge variant="outline" className="bg-white/80 text-xs">
            was {page.originalPageNum}
          </Badge>
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20">
          <GripVertical className="w-8 h-8 text-white" />
        </div>
      </div>
    </Card>
  );
}

function RearrangePdfPages() {
  const [file, setFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pages, setPages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Rearrange PDF Pages - Reorder PDF - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      await loadPdf(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      await loadPdf(droppedFile);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const loadPdf = async (pdfFile) => {
    setIsProcessing(true);
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pageItems = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 0.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({ canvasContext: context, viewport }).promise;
        pageItems.push({
          id: `page-${i}`,
          originalPageNum: i,
          dataUrl: canvas.toDataURL()
        });
      }
      
      setPages(pageItems);
      toast.success(`PDF loaded: ${pdf.numPages} pages. Drag to reorder.`);
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Failed to load PDF");
    }
    setIsProcessing(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setPages((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const resetOrder = () => {
    const sorted = [...pages].sort((a, b) => a.originalPageNum - b.originalPageNum);
    setPages(sorted);
    toast.success("Page order reset");
  };

  const reverseOrder = () => {
    setPages([...pages].reverse());
    toast.success("Page order reversed");
  };

  const saveReorderedPdf = async () => {
    if (!pdfBytes || pages.length === 0) return;

    setIsProcessing(true);
    try {
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      const newPdfDoc = await PDFDocument.create();
      
      // Copy pages in new order
      const pageIndices = pages.map(p => p.originalPageNum - 1);
      const copiedPages = await newPdfDoc.copyPages(pdfDoc, pageIndices);
      copiedPages.forEach(page => newPdfDoc.addPage(page));
      
      const newPdfBytes = await newPdfDoc.save();
      const blob = new Blob([newPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace('.pdf', '_reordered.pdf');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Reordered PDF downloaded!");
    } catch (error) {
      console.error("Error saving PDF:", error);
      toast.error("Failed to save PDF: " + error.message);
    }
    setIsProcessing(false);
  };

  const clearFile = () => {
    setFile(null);
    setPdfBytes(null);
    setPages([]);
  };

  const isReordered = pages.some((page, index) => page.originalPageNum !== index + 1);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-violet-50 to-purple-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <ArrowUpDown className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-full mb-4">
            <ArrowUpDown className="w-5 h-5" />
            <span className="font-medium">Rearrange PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Rearrange PDF Pages
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Drag and drop to reorder pages in your PDF document.
          </p>
        </div>

        {/* Upload Area */}
        {!file && (
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
              isDragging ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-12 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-violet-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select a PDF to rearrange pages
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {file && (
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Controls */}
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <FileText className="w-8 h-8 text-violet-500" />
                  <div>
                    <h3 className="font-semibold dark:text-white">{file.name}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {pages.length} pages {isReordered && '• Modified'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={resetOrder}>
                    Reset Order
                  </Button>
                  <Button variant="outline" size="sm" onClick={reverseOrder}>
                    Reverse
                  </Button>
                  <Button variant="outline" size="sm" onClick={clearFile}>
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Page Grid */}
            {isProcessing && pages.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={pages.map(p => p.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {pages.map((page, index) => (
                      <SortablePage key={page.id} page={page} index={index} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Save Button */}
            <div className="text-center">
              <Button 
                onClick={saveReorderedPdf}
                disabled={isProcessing}
                size="lg"
                className="bg-gradient-to-r from-violet-500 to-purple-600"
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                Save Reordered PDF
              </Button>
            </div>
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default RearrangePdfPages;
